// 시세수집 화면
// 기능: AUTO 종목 시세 수집(저장) / 실시간조회(미저장), 스케줄 설정, 최신 시세 목록
import React, { useState, useEffect, useCallback } from 'react';
import {
  collectPrices,
  fetchLatestPrices,
  fetchScheduleConfig,
  saveScheduleConfig,
  collectStockHistory,
} from '../../api/price';
import { collectMarketHistory } from '../../api/marketIndex';
import PageLayout from '../../components/PageLayout';
import './PriceCollectPage.css';

// ── 날짜 헬퍼 ────────────────────────────────────────────────
const toYMD = (d) => d.toISOString().slice(0, 10);
const oneYearAgo = () => {
  const d = new Date();
  d.setFullYear(d.getFullYear() - 1);
  return toYMD(d);
};
const fiveYearsAgo = () => {
  const d = new Date();
  d.setFullYear(d.getFullYear() - 5);
  return toYMD(d);
};

// ── 포맷 헬퍼 ────────────────────────────────────────────────
const fPrice = (v) => {
  if (v == null) return '-';
  const n = Number(v);
  return n % 1 !== 0
    ? n.toLocaleString('ko-KR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    : n.toLocaleString('ko-KR');
};

const fRate = (v) => {
  if (v == null) return '-';
  const n = Number(v);
  const sign = n > 0 ? '+' : '';
  return `${sign}${n.toFixed(2)}%`;
};

const getDiffStyle = (v) => {
  if (v == null) return {};
  return { color: v > 0 ? '#FF0000' : v < 0 ? '#0000FF' : '#888' };
};

// 수집일자 경과 배지
const getElapsedBadge = (priceDate) => {
  if (!priceDate) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const d = new Date(priceDate);
  d.setHours(0, 0, 0, 0);
  const days = Math.round((today - d) / (1000 * 60 * 60 * 24));
  if (days === 0) return <span className="elapsed-badge elapsed-today">오늘</span>;
  if (days === 1) return <span className="elapsed-badge elapsed-1d">1일 전</span>;
  if (days <= 4)  return <span className="elapsed-badge elapsed-few">{days}일 전</span>;
  return <span className="elapsed-badge elapsed-old">{days}일 전 ⚠️</span>;
};

// ── 메인 컴포넌트 ─────────────────────────────────────────────
const PriceCollectPage = () => {
  // 로딩 상태
  const [collecting,    setCollecting]    = useState(false);
  const [savingSchedule, setSavingSchedule] = useState(false);

  // 데이터 상태
  const [latestPrices,  setLatestPrices]  = useState([]);
  const [collectResult, setCollectResult] = useState(null); // 수집 결과
  const [message,       setMessage]       = useState({ text: '', type: '' });

  // 스케줄 설정 상태
  const [schedule,         setSchedule]         = useState({ use_schedule: false, cron_expr: '0 9,11,13,15,16 * * 1-5' });
  const [nextRun,          setNextRun]           = useState(null);
  const [showSchedule,     setShowSchedule]     = useState(false); // 설정 패널 토글
  const [scheduleEditing,  setScheduleEditing]  = useState(false); // 수정 모드

  // 수집 대상 체크박스
  const [collectStocks, setCollectStocks] = useState(true);
  const [collectIndex,  setCollectIndex]  = useState(true);

  // 시장지수 과거데이터 수집
  const [histStart,  setHistStart]  = useState(oneYearAgo);
  const [histEnd,    setHistEnd]    = useState(toYMD(new Date()));
  const [histBusy,   setHistBusy]   = useState(false);
  const [histResult, setHistResult] = useState(null); // { collected: N } | { error: '...' }

  // 종목 과거시세 수집
  const [stockHistStart,  setStockHistStart]  = useState(fiveYearsAgo);
  const [stockHistEnd,    setStockHistEnd]    = useState(toYMD(new Date()));
  const [stockHistBusy,   setStockHistBusy]   = useState(false);
  const [stockHistResult, setStockHistResult] = useState(null);

  const showMessage = (text, type = 'info') => {
    setMessage({ text, type });
    setTimeout(() => setMessage({ text: '', type: '' }), 4000);
  };

  // 최신 시세 목록 로드
  const loadLatestPrices = useCallback(async () => {
    try {
      const res = await fetchLatestPrices();
      setLatestPrices(res.data || []);
    } catch {
      showMessage('최신 시세 조회에 실패했습니다.', 'error');
    }
  }, []);

  // 스케줄 설정 로드 (마운트 시 DB에서 ON/OFF 상태 복원)
  const loadScheduleConfig = useCallback(async () => {
    try {
      const res = await fetchScheduleConfig();
      setSchedule({ use_schedule: res.use_schedule, cron_expr: res.cron_expr });
      setNextRun(res.next_run || null);
    } catch {
      showMessage('스케줄 설정 조회에 실패했습니다.', 'error');
    }
  }, []);

  // 마운트 시 초기 데이터 로드
  useEffect(() => {
    loadLatestPrices();
    loadScheduleConfig();
  }, [loadLatestPrices, loadScheduleConfig]);

  // ── 시세수집 (저장) ───────────────────────────────────────
  const handleCollect = async () => {
    if (!collectStocks && !collectIndex) {
      showMessage('종목 시세 또는 시장지수 중 하나 이상 선택하세요.', 'warn');
      return;
    }
    setCollecting(true);
    setCollectResult(null);
    try {
      const res = await collectPrices(true, collectStocks, collectIndex);
      setCollectResult({ ...res, mode: '시세수집(저장)' });
      showMessage(res.message, res.fail_count > 0 ? 'warn' : 'success');
      await loadLatestPrices(); // 저장 후 목록 갱신
    } catch {
      showMessage('시세 수집에 실패했습니다.', 'error');
    } finally {
      setCollecting(false);
    }
  };

  // ── 실시간조회 (미저장) ───────────────────────────────────
  const handleRealtime = async () => {
    setCollecting(true);
    setCollectResult(null);
    try {
      const res = await collectPrices(false, collectStocks, false); // 실시간조회: 시장지수 저장 없음
      setCollectResult({ ...res, mode: '실시간조회(미저장)' });
      showMessage(res.message, res.fail_count > 0 ? 'warn' : 'success');
      // 저장 안 함 → 목록 갱신 없음
    } catch {
      showMessage('실시간 조회에 실패했습니다.', 'error');
    } finally {
      setCollecting(false);
    }
  };

  // ── 자동수집 ON/OFF 토글 (즉시 저장) ─────────────────────
  const handleToggleAutoCollect = async () => {
    const newVal = !schedule.use_schedule;
    setSchedule(s => ({ ...s, use_schedule: newVal }));
    try {
      const res = await saveScheduleConfig(newVal, schedule.cron_expr);
      if (res.success === false) {
        // 스케줄러 등록 실패 — DB가 N으로 되돌아왔으므로 UI도 원복
        setSchedule(s => ({ ...s, use_schedule: false }));
        setNextRun(null);
        showMessage(res.message || 'cron 표현식 오류로 스케줄러 등록에 실패했습니다.', 'error');
        return;
      }
      setNextRun(res.next_run || null);
      showMessage(`자동수집이 ${newVal ? 'ON' : 'OFF'}으로 변경되었습니다.`, 'success');
    } catch {
      setSchedule(s => ({ ...s, use_schedule: !newVal })); // 통신 실패 시 원복
      showMessage('자동수집 설정 변경에 실패했습니다.', 'error');
    }
  };

  // ── 스케줄 설정 저장 (cron 표현식) ───────────────────────
  const handleSaveSchedule = async () => {
    setSavingSchedule(true);
    try {
      const res = await saveScheduleConfig(schedule.use_schedule, schedule.cron_expr);
      if (res.success === false) {
        setSchedule(s => ({ ...s, use_schedule: false }));
        setNextRun(null);
        showMessage(res.message || '스케줄러 등록에 실패했습니다. cron 표현식을 확인해 주세요.', 'error');
        return;
      }
      setNextRun(res.next_run || null);
      showMessage('스케줄 설정이 저장되었습니다.', 'success');
      setScheduleEditing(false);
    } catch {
      showMessage('스케줄 설정 저장에 실패했습니다.', 'error');
    } finally {
      setSavingSchedule(false);
    }
  };

  // ── 종목 과거시세 수집 ────────────────────────────────────
  const handleStockHistCollect = async () => {
    if (!stockHistStart || !stockHistEnd) {
      showMessage('시작일과 종료일을 모두 입력하세요.', 'warn');
      return;
    }
    setStockHistBusy(true);
    setStockHistResult(null);
    try {
      const res = await collectStockHistory(stockHistStart, stockHistEnd);
      setStockHistResult({ message: res.message, success_count: res.success_count, total_rows: res.total_rows });
      showMessage(res.message, 'success');
      await loadLatestPrices();
    } catch (e) {
      const msg = e?.response?.data?.detail || '종목 과거시세 수집에 실패했습니다.';
      setStockHistResult({ error: msg });
      showMessage(msg, 'error');
    } finally {
      setStockHistBusy(false);
    }
  };

  // ── 시장지수 과거데이터 수집 ──────────────────────────────
  const handleHistCollect = async () => {
    if (!histStart || !histEnd) {
      showMessage('시작일과 종료일을 모두 입력하세요.', 'warn');
      return;
    }
    setHistBusy(true);
    setHistResult(null);
    try {
      const res = await collectMarketHistory(histStart, histEnd);
      setHistResult({ collected: res.collected });
      showMessage(`시장지수 과거데이터 수집 완료: ${res.collected}건`, 'success');
    } catch (e) {
      const msg = e.response?.data?.detail || e.message;
      setHistResult({ error: msg });
      showMessage('과거데이터 수집 실패: ' + msg, 'error');
    } finally {
      setHistBusy(false);
    }
  };

  // AUTO 종목만 표시
  const autoPrices = latestPrices.filter(p => p.price_type !== 'MANUAL');

  // ── 툴바 ─────────────────────────────────────────────────
  const toolbar = (
    <div className="pc-toolbar">
      <button
        className="btn btn-primary"
        onClick={handleCollect}
        disabled={collecting}
      >
        {collecting ? '수집 중...' : '시세수집'}
      </button>
      <button
        className="btn btn-secondary"
        onClick={handleRealtime}
        disabled={collecting}
      >
        {collecting ? '조회 중...' : '실시간조회'}
      </button>
      <span className="pc-toolbar-sep" />
      <label className="pc-check-label">
        <input
          type="checkbox"
          checked={collectStocks}
          onChange={e => setCollectStocks(e.target.checked)}
          disabled={collecting}
        />
        종목 시세 수집
      </label>
      <label className="pc-check-label">
        <input
          type="checkbox"
          checked={collectIndex}
          onChange={e => setCollectIndex(e.target.checked)}
          disabled={collecting}
        />
        시장지수 수집
      </label>
      <span className="pc-toolbar-sep" />
      <button
        className={`btn btn-outline ${showSchedule ? 'btn-outline-active' : ''}`}
        onClick={() => setShowSchedule(s => !s)}
      >
        스케줄 설정 {showSchedule ? '▲' : '▼'}
      </button>
      <span className="pc-schedule-badge">
        자동수집:&nbsp;
        <strong className={schedule.use_schedule ? 'badge-on' : 'badge-off'}>
          {schedule.use_schedule ? 'ON' : '비활성화'}
        </strong>
        {collecting && <span className="badge-collecting"> 수집 실행중...</span>}
        {!collecting && schedule.use_schedule && nextRun && (
          <span className="badge-next-run-active"> {nextRun}</span>
        )}
      </span>
    </div>
  );

  // ── JSX ──────────────────────────────────────────────────
  return (
    <PageLayout title="시세수집" toolbar={toolbar} message={message}>
      <div className="pc-body">

        {/* ── 스케줄 설정 패널 ── */}
        {showSchedule && (
          <div className="pc-card pc-schedule-card">
            <div className="pc-card-header">
              스케줄 설정
              <button
                className="btn btn-xs btn-outline"
                onClick={() => setScheduleEditing(e => !e)}
              >
                {scheduleEditing ? '취소' : '수정'}
              </button>
            </div>
            <div className="pc-card-body schedule-body">
              {/* 자동수집 ON/OFF — 항상 클릭 가능, 즉시 저장 */}
              <div className="schedule-row">
                <span className="schedule-label">자동수집 사용</span>
                <button
                  className={`toggle-btn ${schedule.use_schedule ? 'toggle-on' : 'toggle-off'}`}
                  onClick={handleToggleAutoCollect}
                >
                  {schedule.use_schedule ? 'ON' : 'OFF'}
                </button>
              </div>

              {/* 스케줄 상태: ON이면 다음 수집 예정, OFF이면 비활성화 안내 */}
              <div className="schedule-row">
                <span className="schedule-label">스케줄 상태</span>
                {schedule.use_schedule ? (
                  nextRun
                    ? <span className="schedule-next-run">다음 수집 예정: {nextRun}</span>
                    : <span className="schedule-next-run">활성화 (다음 실행 계산 중...)</span>
                ) : (
                  <span className="schedule-deactivated">자동수집 비활성화 상태</span>
                )}
              </div>

              {/* 수집 스케줄 (cron 표현식) */}
              <div className="schedule-row">
                <span className="schedule-label">수집 스케줄</span>
                {scheduleEditing ? (
                  <input
                    type="text"
                    className="schedule-input"
                    value={schedule.cron_expr}
                    onChange={e => setSchedule(s => ({ ...s, cron_expr: e.target.value }))}
                    placeholder="0 9,11,13,15,16 * * 1-5"
                  />
                ) : (
                  <span className="schedule-value">{schedule.cron_expr}</span>
                )}
              </div>

              <p className="schedule-hint">
                cron 형식: 분 시 일 월 요일 (예: <code>0 9,11,13,15,16 * * 1-5</code> = 평일 9·11·13·15·16시)
              </p>
              <p className="schedule-hint">
                요일(APScheduler 기준): <code>*</code> 매일 &nbsp;|&nbsp;
                <code>0</code> 월 · <code>1</code> 화 · <code>2</code> 수 · <code>3</code> 목 · <code>4</code> 금 · <code>5</code> 토 · <code>6</code> 일 &nbsp;|&nbsp;
                <code>1-5</code> 월~금
              </p>

              {scheduleEditing && (
                <button
                  className="btn btn-primary"
                  onClick={handleSaveSchedule}
                  disabled={savingSchedule}
                >
                  {savingSchedule ? '저장 중...' : '저장'}
                </button>
              )}
            </div>
          </div>
        )}

        {/* ── 수집 결과 ── */}
        {collectResult && (
          <div className="pc-card pc-result-card">
            <div className="pc-card-header">
              수집 결과
              <span className="result-mode">{collectResult.mode}</span>
              <span className="result-counts">
                성공 <strong className="result-success">{collectResult.success_count}</strong>건 /
                실패 <strong className="result-fail">{collectResult.fail_count}</strong>건
              </span>
              <button
                className="btn btn-xs btn-outline"
                onClick={() => setCollectResult(null)}
              >
                닫기
              </button>
            </div>

            {/* 시장지수 (저장 시) */}
            {collectResult.saved && collectResult.market_index && (
              <div className="market-index-row">
                {[
                  ['USD/KRW', collectResult.market_index.usd_krw, 2],
                  ['코스피',  collectResult.market_index.kospi,   2],
                  ['코스닥',  collectResult.market_index.kosdaq,  2],
                  ['S&P500',  collectResult.market_index.sp500,   2],
                  ['나스닥',  collectResult.market_index.nasdaq,  2],
                  ['금($/oz)',collectResult.market_index.gold,    2],
                ].map(([label, val, dp]) => (
                  <div key={label} className="market-index-item">
                    <span className="mi-label">{label}</span>
                    <span className="mi-value">
                      {val != null ? Number(val).toLocaleString('ko-KR', { minimumFractionDigits: dp, maximumFractionDigits: dp }) : '-'}
                    </span>
                  </div>
                ))}
              </div>
            )}

            {/* 종목별 결과 테이블 */}
            <div className="pc-result-table-wrap">
              <table className="grid-table pc-result-table">
                <thead>
                  <tr>
                    <th>종목명</th>
                    <th>티커</th>
                    <th>시장</th>
                    <th className="col-right">현재가</th>
                    <th className="col-right">등락률</th>
                    <th>상태</th>
                    <th>오류</th>
                  </tr>
                </thead>
                <tbody>
                  {collectResult.results.map((r, i) => (
                    <tr key={i} className={r.status === 'fail' ? 'row-fail' : ''}>
                      <td>{r.stock_name || '-'}</td>
                      <td>{r.ticker}</td>
                      <td className="col-center">{r.market_type}</td>
                      <td className="col-right">{fPrice(r.current_price)}</td>
                      <td className="col-right" style={getDiffStyle(r.diff_rate)}>
                        {fRate(r.diff_rate)}
                      </td>
                      <td className={`col-center status-${r.status}`}>
                        {r.status === 'success' ? '✓ 성공' : '✗ 실패'}
                      </td>
                      <td className="td-error">{r.error || ''}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* ── 최신 시세 목록 (AUTO) ── */}
        <div className="pc-card">
          <div className="pc-card-header">
            최신 시세 목록 (자동수집 종목)
            <button className="btn btn-xs btn-outline" onClick={loadLatestPrices}>새로고침</button>
          </div>
          <div className="pc-table-wrap">
            <table className="grid-table pc-price-table">
              <thead>
                <tr>
                  <th>종목명</th>
                  <th>티커</th>
                  <th className="col-center">시장</th>
                  <th className="col-right">현재가</th>
                  <th className="col-right">전일대비</th>
                  <th className="col-right">등락률</th>
                  <th className="col-center">수집일자</th>
                  <th className="col-center">수집시각</th>
                </tr>
              </thead>
              <tbody>
                {autoPrices.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="grid-empty">
                      시세수집 버튼을 클릭하면 데이터가 표시됩니다.
                    </td>
                  </tr>
                ) : (
                  autoPrices.map((p, i) => (
                    <tr key={i}>
                      <td>{p.stock_name || '-'}</td>
                      <td>{p.ticker}</td>
                      <td className="col-center">{p.market_type || '-'}</td>
                      <td className="col-right price-cell">{fPrice(p.current_price)}</td>
                      <td className="col-right" style={getDiffStyle(p.prev_diff)}>
                        {p.prev_diff != null ? fPrice(p.prev_diff) : '-'}
                      </td>
                      <td className="col-right" style={getDiffStyle(p.diff_rate)}>
                        {fRate(p.diff_rate)}
                      </td>
                      <td className="col-center">
                        <div className="elapsed-cell">
                          {p.price_date || '-'}
                          {p.price_date && getElapsedBadge(p.price_date)}
                        </div>
                      </td>
                      <td className="col-center">
                        {p.updated_at ? p.updated_at.slice(11, 19) : '-'}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* ── 시장지수 과거데이터 수집 ── */}
        <div className="pc-card pc-hist-card">
          <div className="pc-card-header">시장지수 과거데이터 수집</div>
          <div className="pc-card-body pc-hist-body">
            <label className="pc-hist-label">시작일</label>
            <input
              type="date"
              className="pc-hist-date"
              value={histStart}
              onChange={e => setHistStart(e.target.value)}
              disabled={histBusy}
            />
            <label className="pc-hist-label">종료일</label>
            <input
              type="date"
              className="pc-hist-date"
              value={histEnd}
              onChange={e => setHistEnd(e.target.value)}
              disabled={histBusy}
            />
            <button
              className="btn btn-primary"
              onClick={handleHistCollect}
              disabled={histBusy}
            >
              {histBusy ? '수집 중...' : '수집 실행'}
            </button>
            {histBusy && <span className="pc-hist-progress">수집 중...</span>}
            {histResult && !histBusy && (
              <span className={`pc-hist-result ${histResult.error ? 'error' : 'success'}`}>
                {histResult.error ? `오류: ${histResult.error}` : `완료 ${histResult.collected}건`}
              </span>
            )}
          </div>
          <p className="pc-hist-hint">
            yfinance로 전체 시장지수(환율·지수·원자재)를 날짜별로 일괄 수집합니다.
            이미 있는 날짜는 덮어씁니다. 수집 후 시장지수현황 화면에서 확인하세요.
          </p>
        </div>

        {/* ── 종목 과거시세 수집 ── */}
        <div className="pc-card pc-hist-card">
          <div className="pc-card-header">종목 과거시세 수집 (OHLCV)</div>
          <div className="pc-card-body pc-hist-body">
            <label className="pc-hist-label">시작일</label>
            <input
              type="date"
              className="pc-hist-date"
              value={stockHistStart}
              onChange={e => setStockHistStart(e.target.value)}
              disabled={stockHistBusy}
            />
            <label className="pc-hist-label">종료일</label>
            <input
              type="date"
              className="pc-hist-date"
              value={stockHistEnd}
              onChange={e => setStockHistEnd(e.target.value)}
              disabled={stockHistBusy}
            />
            <button
              className="btn btn-primary"
              onClick={handleStockHistCollect}
              disabled={stockHistBusy}
            >
              {stockHistBusy ? '수집 중...' : '수집 실행'}
            </button>
            {stockHistBusy && <span className="pc-hist-progress">수집 중... (시간이 걸릴 수 있습니다)</span>}
            {stockHistResult && !stockHistBusy && (
              <span className={`pc-hist-result ${stockHistResult.error ? 'error' : 'success'}`}>
                {stockHistResult.error
                  ? `오류: ${stockHistResult.error}`
                  : stockHistResult.message}
              </span>
            )}
          </div>
          <p className="pc-hist-hint">
            yfinance로 AUTO 종목의 날짜별 시가·고가·저가·종가·거래량을 수집합니다.
            이미 있는 날짜는 덮어씁니다. 수집 후 차트 팝업에서 확인하세요.
          </p>
        </div>


      </div>
    </PageLayout>
  );
};

export default PriceCollectPage;
